import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Upload, Download, Plus, Trash2, Package, ScanLine, ArrowRightLeft, FileText, ShieldCheck, Calendar, AlertTriangle, FileSignature, CheckCircle2, Clock, Eraser, Wrench, ArrowLeftCircle, Pencil, X, Sparkles, Loader2 } from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";
import BollaModal from "@/components/BollaModal";
import { SignatureCanvas } from "react-signature-canvas";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const DOC_CATEGORIES = [
  { value: "pos", label: "POS (Piano Operativo Sicurezza)", color: "bg-red-50 text-red-700 border-red-200", icon: ShieldCheck },
  { value: "attestato", label: "Attestato (corso altezza, ecc.)", color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: FileText },
  { value: "documento_identita", label: "Documento identità", color: "bg-blue-50 text-blue-700 border-blue-200", icon: FileText },
  { value: "patente", label: "Patente", color: "bg-amber-50 text-amber-700 border-amber-200", icon: FileText },
  { value: "altro", label: "Altro documento", color: "bg-slate-50 text-slate-700 border-slate-200", icon: FileText },
];

export default function MagazzinoViaggiante() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isTechRole = user?.role === "tecnico";
  const [technicians, setTechnicians] = useState([]);
  const [tech, setTech] = useState("");
  const [serials, setSerials] = useState([]);
  const [serialCounts, setSerialCounts] = useState({});
  const [materials, setMaterials] = useState([]);
  const [committenti, setCommittenti] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [equipment, setEquipment] = useState([]);

  const [openImport, setOpenImport] = useState(false);
  const [impMaterial, setImpMaterial] = useState("");
  const [impCommittente, setImpCommittente] = useState("");
  const [impFile, setImpFile] = useState(null);

  // Filtro materiale per lista seriali in viaggio
  const [serialMaterialFilter, setSerialMaterialFilter] = useState("__all__");
  const [serialCommittenteFilter, setSerialCommittenteFilter] = useState("__all__");
  // Filtri periodo (da → a) indipendenti per ogni sezione (YYYY-MM-DD)
  const [serialDateFrom, setSerialDateFrom] = useState("");
  const [serialDateTo, setSerialDateTo] = useState("");
  const [supplyDateFrom, setSupplyDateFrom] = useState("");
  const [supplyDateTo, setSupplyDateTo] = useState("");
  const [bolleDateFrom, setBolleDateFrom] = useState("");
  const [bolleDateTo, setBolleDateTo] = useState("");

  // Modifica seriale (material_name, committente)
  const [editSerial, setEditSerial] = useState(null); // { id, serial, material_name, committente_id }
  const [savingEdit, setSavingEdit] = useState(false);
  // Conflitto rilevato in fase di modifica seriale (esiste già in magazzino generale)
  const [editConflict, setEditConflict] = useState(null); // { existing:{...}, newSerial }

  // Aggiunta seriale singolo (con barcode)
  const [addSerial, setAddSerial] = useState("");
  const [addMaterial, setAddMaterial] = useState("");
  const [addCommittente, setAddCommittente] = useState("");
  const [addingSerial, setAddingSerial] = useState(false);

  // AI Photo Scan — rileva più seriali da una foto
  const aiFileRef = useRef(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSerials, setAiSerials] = useState([]);
  const [aiBulkBusy, setAiBulkBusy] = useState(false);

  // Dialog "seriale già esistente" per trasferimento
  const [conflictDialog, setConflictDialog] = useState(null); // { info, targetSerial }

  // Aggiunta materiale di consumo
  const [supMaterialId, setSupMaterialId] = useState("");
  const [supQty, setSupQty] = useState(1);
  const [supNotes, setSupNotes] = useState("");
  const [supAdding, setSupAdding] = useState(false);

  // Documenti tecnico (POS, attestati)
  const [documents, setDocuments] = useState([]);
  const [docOpen, setDocOpen] = useState(false);
  const [docForm, setDocForm] = useState({
    category: "pos",
    title: "",
    description: "",
    issued_date: "",
    expiry_date: "",
    file: null,
  });
  const [docUploading, setDocUploading] = useState(false);

  // Bolle di consegna DDT
  const [bolle, setBolle] = useState([]);
  const [bollaOpen, setBollaOpen] = useState(false);
  const [signingBid, setSigningBid] = useState(null);
  // Focus automatico sull'input seriale dopo un'azione (per sparare barcode consecutivi)
  const addSerialInputRef = useRef(null);
  const focusAddSerialInput = () => {
    // requestAnimationFrame + timeout: assicura che il dialog sia chiuso prima del focus
    setTimeout(() => {
      try { addSerialInputRef.current?.focus(); addSerialInputRef.current?.select?.(); } catch {}
    }, 100);
  };

  useEffect(() => {
    if (isTechRole) {
      // Il tecnico vede solo se stesso, senza dropdown
      setTechnicians([{ id: user.id, name: user.name, email: user.email }]);
      setTech(user.id);
    } else {
      api.get("/users", { params: { role: "tecnico,subappalto" } }).then((r) => {
        setTechnicians(r.data);
        if (r.data.length && !tech) setTech(r.data[0].id);
      });
    }
    api.get("/materials").then((r) => {
      setMaterials(r.data);
      if (r.data.length) {
        setImpMaterial(r.data[0].name);
        setAddMaterial(r.data[0].name);
      }
    }).catch(() => {});
    api.get("/committenti").then((r) => setCommittenti(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const load = async (id) => {
    if (!id) return;
    const requests = [
      api.get("/serials", { params: { technician_id: id, status: "traveling" } }).catch(() => {
        toast.error("Impossibile caricare le matricole del furgone, riprova tra poco");
        return { data: [] };
      }),
      api.get("/bolle", { params: { technician_id: id } }).catch(() => ({ data: [] })),
      // Non filtriamo per status="assigned": vogliamo mostrare anche gli item
      // marcati come "repair" o "lost" nella sezione dedicata "Attrezzatura persa/rotta".
      // Il backend ritorna comunque solo item con technician_id != null quando passato,
      // e repair/lost mantengono il tech_id (vedi set_equipment_status).
      api.get("/equipment", { params: { technician_id: id } }).catch(() => ({ data: [] })),
      api.get("/technician-supplies", { params: { technician_id: id } }).catch(() => ({ data: [] })),
      api.get("/technician-documents", { params: { technician_id: id } }).catch(() => ({ data: [] })),
    ];
    const results = await Promise.all(requests);
    setSerials(results[0].data);
    setBolle(results[1].data);
    setEquipment(results[2].data);
    setSupplies(results[3].data);
    setDocuments(results[4].data);
    if (!isTechRole) api.get("/serials/traveling-counts").then((r) => setSerialCounts(r.data || {})).catch(() => {});
  };

  useEffect(() => { if (tech) load(tech); }, [tech]);

  const doUnload = async (serial) => {
    try {
      await api.post("/serials/unload", { serial, reason: "reso a magazzino" });
      toast.success(t("auto.rientrato_in_magazzino_generale"));
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doImport = async () => {
    if (!tech) return toast.error(t("auto.seleziona_un_tecnico"));
    if (!impMaterial || !impFile) return toast.error(t("auto.materiale_e_file_richiesti"));
    const form = new FormData();
    form.append("file", impFile);
    try {
      const params = new URLSearchParams({ technician_id: tech, material_name: impMaterial });
      if (impCommittente) params.append("committente_id", impCommittente);
      const { data } = await api.post(
        `/serials/upload-excel-tecnico?${params.toString()}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`${data.count_created} nuovi + ${data.count_transferred} trasferiti (${data.count_skipped} scartati)`);
      setOpenImport(false);
      setImpFile(null);
      setImpCommittente("");
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/downloads/serials-template-tecnico.xlsx", {
        responseType: "blob",
        params: { t: Date.now() },  // cache-buster
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "template_seriali_tecnico.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore download template"); }
  };

  // --- SERIALE SINGOLO ---
  // Scatta foto → AI estrae tutti i seriali visibili
  const handleAiScan = async (file) => {
    setAiBusy(true);
    setAiSerials([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/serials/detect-from-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const list = Array.isArray(data?.serials) ? data.serials : [];
      if (list.length === 0) {
        toast.error("Nessun seriale rilevato — riprova con foto più nitida");
      } else {
        toast.success(`Trovati ${list.length} seriali`);
        setAiSerials(list);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore analisi AI");
    } finally {
      setAiBusy(false);
    }
  };

  // Aggiunge in blocco tutti i seriali rilevati dall'AI sul furgone del tecnico corrente.
  // Per ogni seriale: se non esiste → crea. Se esiste con lookup, salta e mostra motivo.
  const bulkAddDetected = async () => {
    if (!tech) return toast.error(t("auto.seleziona_un_tecnico"));
    if (!addMaterial) return toast.error(t("auto.materiale_mancante"));
    if (aiSerials.length === 0) return;
    setAiBulkBusy(true);
    let created = 0;
    let skipped = 0;
    for (const s of aiSerials) {
      try {
        const { data: lookup } = await api.get("/serials/lookup", { params: { serial: s } });
        if (lookup.found) { skipped++; continue; }
        await api.post("/serials", {
          serial: s,
          material_name: addMaterial,
          technician_id: tech,
          committente_id: addCommittente || undefined,
        });
        created++;
      } catch (e) {
        skipped++;
      }
    }
    if (created > 0) toast.success(`${created} seriali caricati sul furgone`);
    if (skipped > 0) toast.info(`${skipped} già esistenti — non aggiunti`);
    setAiSerials([]);
    load(tech);
    setAiBulkBusy(false);
  };

  const submitAddSerial = async (serialOverride) => {    if (!tech) return toast.error(t("auto.seleziona_un_tecnico"));
    const s = (serialOverride ?? addSerial).trim();
    if (!s) return toast.error("Seriale mancante");
    if (!addMaterial) return toast.error(t("auto.materiale_mancante"));

    setAddingSerial(true);
    try {
      // 1) Cerca se il seriale esiste già
      const { data: lookup } = await api.get("/serials/lookup", { params: { serial: s } });
      if (lookup.found) {
        // Se è già assegnato allo stesso tecnico, avvisa e stop
        if (lookup.technician_id === tech && lookup.status === "traveling") {
          toast.info(`Seriale ${s} è già sul furgone di ${selectedTech?.name}`);
          setAddSerial("");
          focusAddSerialInput();
          setAddingSerial(false);
          return;
        }
        // Se è installato, blocca
        if (lookup.status === "installed") {
          toast.error(`Seriale ${s} è già installato (${lookup.installed_address || lookup.installed_customer || ""})`);
          focusAddSerialInput();
          setAddingSerial(false);
          return;
        }
        // Altrimenti apri dialog di conferma trasferimento
        setConflictDialog({ info: lookup, targetSerial: s });
        setAddingSerial(false);
        return;
      }

      // 2) Non esiste → crea diretto
      await api.post("/serials", {
        serial: s,
        material_name: addMaterial,
        technician_id: tech,
        committente_id: addCommittente || undefined,
      });
      toast.success(`Seriale ${s} caricato sul furgone di ${selectedTech?.name || "tecnico"}`);
      setAddSerial("");
      focusAddSerialInput();
      load(tech);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      focusAddSerialInput();
    } finally {
      setAddingSerial(false);
    }
  };

  // Conferma trasferimento del seriale conflittuale al tecnico corrente
  const confirmTransfer = async () => {
    if (!conflictDialog) return;
    const { info } = conflictDialog;
    try {
      await api.patch(`/serials/${info.id}`, {
        status: "traveling",
        technician_id: tech,
        ...(addCommittente ? { committente_id: addCommittente } : {}),
      });
      toast.success(`Seriale ${info.serial} trasferito a ${selectedTech?.name}`);
      setAddSerial("");
      setConflictDialog(null);
      focusAddSerialInput();
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); focusAddSerialInput(); }
  };

  // --- Cambio stato manuale di un seriale (magazziniere) ---
  const changeSerialStatus = async (serialId, newStatus) => {
    if (!window.confirm(`Cambiare lo stato del seriale in "${newStatus}"?`)) return;
    try {
      await api.patch(`/serials/${serialId}`, { status: newStatus });
      toast.success(`Stato aggiornato: ${newStatus}`);
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveEditSerial = async (opts = {}) => {
    if (!editSerial?.id) return;
    const newSerial = (editSerial.serial || "").trim();
    if (!newSerial) return toast.error("Il seriale non può essere vuoto");
    setSavingEdit(true);
    try {
      await api.patch(`/serials/${editSerial.id}`, {
        serial: newSerial,
        material_name: editSerial.material_name || "",
        committente_id: editSerial.committente_id || "",
        ...(opts.forceReplace ? { force_replace_existing: true } : {}),
      });
      toast.success(`Seriale ${newSerial} aggiornato`);
      setEditSerial(null);
      setEditConflict(null);
      load(tech);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Conflitto strutturato: se il duplicato è in magazzino generale, chiedi conferma per spostarlo
      if (detail && typeof detail === "object" && detail.code === "SERIAL_CONFLICT" && ["warehouse", "traveling"].includes(detail.existing?.status)) {
        setEditConflict({ existing: detail.existing, newSerial });
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally { setSavingEdit(false); }
  };

  const deleteSerial = async (s) => {
    if (!window.confirm(`Eliminare definitivamente il seriale "${s.serial}"?\n\nQuesta operazione NON può essere annullata.`)) return;
    try {
      await api.delete(`/serials/${s.id}`);
      toast.success(`Seriale ${s.serial} eliminato`);
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // --- MATERIALE DI CONSUMO ---
  const selectedSupMaterial = useMemo(() => materials.find((m) => m.id === supMaterialId), [materials, supMaterialId]);
  const supTotal = useMemo(() => {
    const price = Number(selectedSupMaterial?.unit_price ?? 0);
    return (price * (Number(supQty) || 0)).toFixed(2);
  }, [selectedSupMaterial, supQty]);

  const submitSupply = async () => {
    if (!tech) return toast.error(t("auto.seleziona_un_tecnico"));
    if (!selectedSupMaterial) return toast.error(t("auto.scegli_un_materiale"));
    if (!supQty || Number(supQty) <= 0) return toast.error(t("auto.quantità_non_valida"));
    setSupAdding(true);
    try {
      await api.post("/technician-supplies", {
        technician_id: tech,
        material_id: selectedSupMaterial.id,
        material_name: selectedSupMaterial.name,
        quantity: Number(supQty),
        unit_price: Number(selectedSupMaterial.unit_price ?? 0),
        notes: supNotes,
      });
      toast.success(t("auto.materiale_consegnato"));
      setSupQty(1);
      setSupNotes("");
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSupAdding(false); }
  };

  const deleteSupply = async (id) => {
    if (!window.confirm("Rimuovere la consegna?")) return;
    try {
      await api.delete(`/technician-supplies/${id}`);
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // --- DOCUMENTI TECNICO ---
  const uploadDocument = async () => {
    if (!tech) return toast.error(t("auto.seleziona_un_tecnico"));
    if (!docForm.file || !docForm.title.trim()) return toast.error("Titolo e file richiesti");
    setDocUploading(true);
    try {
      const fd = new FormData();
      fd.append("technician_id", tech);
      fd.append("category", docForm.category);
      fd.append("title", docForm.title);
      fd.append("description", docForm.description || "");
      if (docForm.issued_date) fd.append("issued_date", docForm.issued_date);
      if (docForm.expiry_date) fd.append("expiry_date", docForm.expiry_date);
      fd.append("file", docForm.file);
      await api.post("/technician-documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      setDocOpen(false);
      setDocForm({ category: "pos", title: "", description: "", issued_date: "", expiry_date: "", file: null });
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDocUploading(false); }
  };

  const deleteDocument = async (id) => {
    if (!window.confirm("Eliminare il documento?")) return;
    try {
      await api.delete(`/technician-documents/${id}`);
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/technician-documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore download"); }
  };

  // --- ATTREZZATURA ---
  const returnEquipment = async (eid) => {
    if (!window.confirm("Far rientrare in magazzino?")) return;
    try {
      await api.post(`/equipment/${eid}/return`);
      toast.success(t("auto.attrezzatura_rientrata_in_magazzino"));
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setEquipmentStatus = async (eid, status, label) => {
    const note = window.prompt(`Nota per marcare come "${label}" (opzionale):`, "");
    if (note === null) return;
    try {
      await api.post(`/equipment/${eid}/status`, { status, note });
      toast.success(`Attrezzatura marcata come ${label}`);
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const generateDisciplinaryLetter = async (eq) => {
    if (!tech) return toast.error("Seleziona prima un tecnico");
    const amtStr = window.prompt(`Importo addebito (€) per "${eq.name}":`, "");
    if (amtStr === null) return;
    const amount = Number(String(amtStr).replace(",", "."));
    if (isNaN(amount) || amount < 0) return toast.error("Importo non valido");
    const reason = window.prompt('Motivazione ("rotto" / "perso" / altro):', "rotto");
    if (reason === null) return;
    const circumstances = window.prompt("Circostanze dell'evento (opzionale, l'AI le userà nel testo):", "") || "";
    try {
      toast.info("Generazione lettera con AI in corso...");
      const res = await api.post(`/equipment/disciplinary-letter`,
        { equipment_id: eq.id, technician_id: tech, amount, reason, circumstances },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Lettera_Disciplinare_${(selectedTech?.name || "tecnico").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Lettera disciplinare generata ✓");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore generazione lettera"); }
  };

  // --- BOLLE DI CONSEGNA ---
  const downloadBollaPdf = async (bid) => {
    try {
      const res = await api.get(`/bolle/${bid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bolla-${bid.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore download bolla"); }
  };

  const signExistingBolla = async (bid, sigDataUrl, sendEmail) => {
    try {
      await api.post(`/bolle/${bid}/sign`, { signature_data_url: sigDataUrl, send_email: sendEmail });
      toast.success(sendEmail ? "Bolla firmata e inviata via email" : "Bolla firmata");
      setSigningBid(null);
      load(tech);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const expiryStatus = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    const exp = new Date(dateStr);
    const days = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
    if (days < 0) return { cls: "bg-red-100 text-red-800 border-red-300", label: `SCADUTO (${Math.abs(days)}g)` };
    if (days <= 30) return { cls: "bg-orange-100 text-orange-800 border-orange-300", label: `Scade tra ${days}g` };
    return { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: `Valido (${days}g)` };
  };

  const selectedTech = technicians.find((x) => x.id === tech);

  // Filtri periodo per singola sezione (da → a inclusivi). Confronto stringa lessicografico su YYYY-MM-DD.
  const _inRange = (dateStr, from, to) => {
    const d = (dateStr || "").slice(0, 10);
    if (!d) return !from && !to;  // record senza data: visibile solo se nessun filtro
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  };

  const filteredSupplies = useMemo(() => {
    if (!supplyDateFrom && !supplyDateTo) return supplies;
    return supplies.filter((s) => _inRange(s.date, supplyDateFrom, supplyDateTo));
  }, [supplies, supplyDateFrom, supplyDateTo]);

  const filteredBolle = useMemo(() => {
    if (!bolleDateFrom && !bolleDateTo) return bolle;
    return bolle.filter((b) => _inRange(b.created_at, bolleDateFrom, bolleDateTo));
  }, [bolle, bolleDateFrom, bolleDateTo]);

  const totalSuppliesValue = filteredSupplies.reduce((sum, s) => sum + Number(s.total || 0), 0);

  // Lista univoca dei materiali presenti nei seriali in viaggio (per popolare il dropdown)
  const serialMaterialsInList = useMemo(() => {
    const set = new Set();
    for (const s of serials) if (s.material_name) set.add(s.material_name);
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [serials]);

  const filteredSerials = useMemo(() => {
    let out = serials;
    if (serialMaterialFilter !== "__all__") out = out.filter((s) => (s.material_name || "") === serialMaterialFilter);
    if (serialCommittenteFilter !== "__all__") {
      out = out.filter((s) => (s.committente_name || "").toLowerCase() === serialCommittenteFilter.toLowerCase());
    }
    if (serialDateFrom || serialDateTo) {
      out = out.filter((s) => _inRange(s.moved_at || s.assigned_at || s.created_at, serialDateFrom, serialDateTo));
    }
    return out;
  }, [serials, serialMaterialFilter, serialCommittenteFilter, serialDateFrom, serialDateTo]);

  const uniqueCommittenti = useMemo(() => {
    const set = new Set(serials.map((s) => s.committente_name).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [serials]);

  // Separa attrezzatura assegnata (attiva) da quella rotta/persa (in attesa di addebito o dismissione)
  const activeEquipment = useMemo(() => equipment.filter((e) => !["repair", "lost"].includes(e.status)), [equipment]);
  const brokenLostEquipment = useMemo(() => equipment.filter((e) => ["repair", "lost"].includes(e.status)), [equipment]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.furgoni_tecnici")}</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.warehouse_traveling")}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isTechRole && (
            <>
              <Button
                onClick={() => setBollaOpen(true)}
                disabled={!tech}
                className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-nuova-bolla"
              >
                <FileSignature className="mr-2 h-4 w-4" /> {t("auto.nuova_bolla_ddt_btn")}
              </Button>
              <Dialog open={openImport} onOpenChange={setOpenImport}>
                <DialogTrigger asChild>
                  <Button className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-import-tecnico" disabled={!tech}>
                    <Upload className="mr-2 h-4 w-4" /> {t("auto.import_excel_al_tecnico")}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t("auto.carica_seriali_direttamente_al_tecnico")}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="rounded-sm bg-slate-50 p-3 text-xs text-slate-600">
                      <b>{selectedTech?.name || "—"}</b><br />
                      I seriali già esistenti vengono trasferiti sul suo furgone.<br />{t("auto.i_seriali_nuovi_vengono_creati_e_assegnati_direttamente")}
                    </div>
                    <div>
                      <Label>{t("auto.materiale_x")}</Label>
                      <Select value={impMaterial} onValueChange={setImpMaterial}>
                        <SelectTrigger className="rounded-sm" data-testid="import-tec-material"><SelectValue /></SelectTrigger>
                        <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t("auto.committente_di_chi_sono_i_modem")}</Label>
                      <Select value={impCommittente || "__none__"} onValueChange={(v) => setImpCommittente(v === "__none__" ? "" : v)}>
                        <SelectTrigger className="rounded-sm" data-testid="import-tec-committente"><SelectValue placeholder={t("auto.scegli_committente")} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t("auto.nessuno")}</SelectItem>
                          {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>File Excel/CSV (colonna "serial")</Label>
                      <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setImpFile(e.target.files?.[0])} className="rounded-sm" data-testid="import-tec-file" />
                      <button type="button" onClick={downloadTemplate} className="mt-2 text-xs font-medium text-[#0052FF] hover:underline">
                        ↓ Scarica template pronto
                      </button>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={doImport} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="import-tec-confirm">{t("auto.carica")}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button variant="ghost" onClick={downloadTemplate} className="rounded-sm text-xs" data-testid="btn-template-tecnico">
                <Download className="mr-1 h-3 w-3" /> {t("auto.template_excel")}
              </Button>
            </>
          )}
        </div>
      </div>

      {!isTechRole && (
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-64">
          <Label className="text-xs text-slate-500">{t("auto.tecnico")}</Label>
          <Select value={tech} onValueChange={setTech}>
            <SelectTrigger className="rounded-sm" data-testid="tech-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {technicians.map((tech) => (
                <SelectItem key={tech.id} value={tech.id} data-testid={`tech-option-${tech.id}`}>
                  {tech.name} · {serialCounts[tech.id] || 0} {t("auto.matricole")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      )}

      {selectedTech && (
        <div className="grid grid-cols-1 gap-4 rounded-sm border border-slate-200 bg-white p-5 md:grid-cols-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.tecnico")}</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">{selectedTech.name}</div>
            <div className="font-mono text-xs text-slate-500">{selectedTech.email}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.matricole_assegnate")}</div>
            <div className="mt-1 font-mono text-2xl font-bold text-[#0052FF]" data-testid="tech-serials-count">{serials.length}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.mezzo")}</div>
            {(() => {
              const assigned = vehicles.find((v) => v.technician_id === selectedTech.id);
              if (assigned) {
                return (
                  <div className="mt-1 space-y-0.5">
                    <div className="font-mono text-base font-semibold text-slate-900">{assigned.plate}</div>
                    {(assigned.name || assigned.model) && (
                      <div className="text-xs text-slate-500">{assigned.name || assigned.model}</div>
                    )}
                  </div>
                );
              }
              return <div className="mt-1 font-mono text-base text-slate-900">{selectedTech.vehicle || "—"}</div>;
            })()}
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.attrezzatura")}</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {(selectedTech.equipment || []).length === 0 && <span className="text-slate-400">—</span>}
              {(selectedTech.equipment || []).map((e, i) => (
                <Badge key={i} variant="secondary" className="rounded-sm">{e}</Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Aggiunta seriale singolo (con barcode) */}
      {tech && (
        <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="add-serial-card">
          <div className="mb-3 flex items-center gap-2">
            <ScanLine className="h-4 w-4 text-[#0052FF]" />
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.aggiungi_matricola_al_furgone")}</div>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
            <div className="md:col-span-4">
              <Label>{t("auto.seriale_barcode")}</Label>
              <div className="flex gap-2">
                <Input
                  ref={addSerialInputRef}
                  value={addSerial}
                  onChange={(e) => setAddSerial(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitAddSerial()}
                  placeholder={t("auto.spara_barcode_scrivi_ph")}
                  className="flex-1 rounded-sm font-mono"
                  data-testid="add-serial-input"
                />
                {!selectedTech?.disable_camera_scan && (
                  <BarcodeScanner
                    triggerLabel=""
                    testid="add-serial-scanner"
                    continuous
                    onDetected={(text) => { setAddSerial(text); submitAddSerial(text); }}
                  />
                )}
              </div>
              {selectedTech?.disable_camera_scan && (
                <div className="mt-1 text-[11px] text-slate-400" data-testid="camera-scan-disabled-hint">
                  🔒 Scansione fotocamera disattivata dall'admin per questo tecnico
                </div>
              )}
              {/* AI photo scan: bulk detection */}
              {!selectedTech?.disable_camera_scan && (
                <div className="mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-sm border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100"
                    onClick={() => aiFileRef.current?.click()}
                    disabled={aiBusy || aiBulkBusy}
                    data-testid="mag-ai-scan-btn"
                  >
                    {aiBusy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Analisi AI…</> : <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Scatta e leggi seriali (AI)</>}
                  </Button>
                  <input
                    ref={aiFileRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      e.target.value = "";
                      handleAiScan(f);
                    }}
                    className="hidden"
                    data-testid="mag-ai-scan-input"
                  />
                </div>
              )}
            </div>
            <div className="md:col-span-3">
              <Label>{t("auto.materiale_x")}</Label>
              <Select value={addMaterial} onValueChange={setAddMaterial}>
                <SelectTrigger className="rounded-sm" data-testid="add-serial-material"><SelectValue /></SelectTrigger>
                <SelectContent>{materials.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label>{t("auto.committente_x")}</Label>
              <Select value={addCommittente || "__none__"} onValueChange={(v) => setAddCommittente(v === "__none__" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="add-serial-committente"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t("auto.nessuno")}</SelectItem>
                  {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button
                onClick={() => submitAddSerial()}
                disabled={addingSerial || !addSerial.trim()}
                className="w-full rounded-sm bg-[#0052FF] hover:bg-[#0040CC]"
                data-testid="add-serial-btn"
              >
                <Plus className="mr-1 h-4 w-4" /> {t("auto.aggiungi")}
              </Button>
            </div>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            {t("auto.seriale_creato_hint", { name: selectedTech?.name || "" })}
          </div>
          {aiSerials.length > 0 && (
            <div className="mt-3 rounded-sm border border-purple-200 bg-purple-50/60 p-3" data-testid="mag-ai-serials-list">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-purple-800">
                  <Sparkles className="mr-1 inline h-3 w-3" /> Seriali rilevati ({aiSerials.length}) — clicca uno per compilare il campo, o carica tutti in blocco
                </span>
                <div className="flex items-center gap-1.5">
                  <Button
                    size="sm"
                    onClick={bulkAddDetected}
                    disabled={aiBulkBusy || !addMaterial}
                    className="rounded-sm bg-purple-600 text-white hover:bg-purple-700"
                    data-testid="mag-ai-bulk-add"
                  >
                    {aiBulkBusy ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Caricamento…</> : <><Plus className="mr-1.5 h-3.5 w-3.5" /> Carica tutti ({aiSerials.length})</>}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setAiSerials([])}
                    className="text-purple-600 hover:text-purple-900"
                    data-testid="mag-ai-serials-clear"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {!addMaterial && (
                <div className="mb-2 rounded-sm border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                  ⚠️ Seleziona prima il materiale per abilitare il caricamento in blocco
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {aiSerials.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { setAddSerial(s); toast.success(`Selezionato: ${s}`); }}
                    className="rounded-sm border border-purple-300 bg-white px-2.5 py-1 font-mono text-xs text-purple-900 hover:border-purple-500 hover:bg-purple-100"
                    data-testid={`mag-ai-serial-pick-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Materiali di consumo */}
      {tech && !isTechRole && (
        <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="supplies-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-emerald-600" />
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                {isTechRole ? t("auto.materiale_consumo_ricevuto_kicker") : t("auto.materiale_consumo_consegnato_kicker")}
              </div>
            </div>
            {!isTechRole && (
              <div className="text-xs text-slate-500">{t("auto.totale_valore_consegnato")} <span className="font-mono font-semibold text-slate-900">€ {totalSuppliesValue.toFixed(2)}</span></div>
            )}
            {isTechRole && supplies.length > 0 && (
              <div className="text-xs text-slate-500">{t("auto.totale_ricevuto")} <span className="font-mono font-semibold text-slate-900">€ {totalSuppliesValue.toFixed(2)}</span></div>
            )}
          </div>

          {!isTechRole && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
            <div className="md:col-span-5">
              <Label>{t("auto.materiale_con_prezzo")}</Label>
              <Select value={supMaterialId} onValueChange={setSupMaterialId}>
                <SelectTrigger className="rounded-sm" data-testid="sup-material"><SelectValue placeholder={t("auto.scegli_materiale")} /></SelectTrigger>
                <SelectContent>
                  {materials.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name} — <span className="font-mono">€ {Number(m.unit_price ?? 0).toFixed(2)}</span>/{m.unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSupMaterial && (
                <div className="mt-1 text-[11px] text-slate-500">
                  Prezzo unitario: <span className="font-mono font-semibold text-slate-900">€ {Number(selectedSupMaterial.unit_price ?? 0).toFixed(2)}</span> · categoria: {selectedSupMaterial.category}
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <Label>{t("common.quantity")}</Label>
              <Input type="number" step="0.01" min="0" value={supQty} onChange={(e) => setSupQty(e.target.value)} className="rounded-sm font-mono" data-testid="sup-qty" />
            </div>
            <div className="md:col-span-2">
              <Label>{t("auto.totale_eur")}</Label>
              <Input value={supTotal} readOnly className="rounded-sm bg-slate-50 font-mono font-semibold" data-testid="sup-total" />
            </div>
            <div className="md:col-span-2">
              <Label>{t("common.notes")}</Label>
              <Input value={supNotes} onChange={(e) => setSupNotes(e.target.value)} className="rounded-sm text-xs" placeholder={t("auto.opzionale_ph")} />
            </div>
            <div className="md:col-span-1 flex items-end">
              <Button onClick={submitSupply} disabled={supAdding || !supMaterialId} className="w-full rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="sup-add-btn">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          )}

          {supplies.length === 0 ? (
            <div className={`${isTechRole ? "" : "mt-4"} rounded-sm border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400`}>
              {t("auto.nessuna_consegna_consumo")}
            </div>
          ) : (
            <div className={`${isTechRole ? "" : "mt-4"} space-y-2`}>
              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-xs text-slate-500">Periodo:</Label>
                <Input
                  type="date"
                  value={supplyDateFrom}
                  onChange={(e) => setSupplyDateFrom(e.target.value)}
                  className="h-8 w-40 rounded-sm text-xs"
                  data-testid="supply-date-from"
                  placeholder="Da"
                />
                <span className="text-xs text-slate-400">→</span>
                <Input
                  type="date"
                  value={supplyDateTo}
                  onChange={(e) => setSupplyDateTo(e.target.value)}
                  className="h-8 w-40 rounded-sm text-xs"
                  data-testid="supply-date-to"
                  placeholder="A"
                />
                {(supplyDateFrom || supplyDateTo) && (
                  <Button size="sm" variant="ghost" onClick={() => { setSupplyDateFrom(""); setSupplyDateTo(""); }} className="h-8 rounded-sm text-xs" data-testid="supply-date-clear">
                    <X className="h-3.5 w-3.5 mr-1" /> Reset
                  </Button>
                )}
                <span className="ml-auto text-xs text-slate-500">Visibili: <span className="font-mono font-semibold text-slate-900">{filteredSupplies.length}</span> / {supplies.length}</span>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="p-2">{t("common.date")}</th>
                    <th className="p-2">{t("common.material")}</th>
                    <th className="p-2 text-right">{t("auto.col_qt")}</th>
                    <th className="p-2 text-right">{t("auto.col_eur_u")}</th>
                    <th className="p-2 text-right">{t("common.total")}</th>
                    <th className="p-2">{t("common.notes")}</th>
                    {!isTechRole && <th className="p-2"></th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredSupplies.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sup-row-${s.id}`}>
                      <td className="p-2 font-mono text-xs">{(s.date || "").slice(0, 10)}</td>
                      <td className="p-2">{s.material_name}</td>
                      <td className="p-2 text-right font-mono">{s.quantity} {s.unit || ""}</td>
                      <td className="p-2 text-right font-mono">€ {Number(s.unit_price).toFixed(2)}</td>
                      <td className="p-2 text-right font-mono font-semibold">€ {Number(s.total).toFixed(2)}</td>
                      <td className="p-2 text-xs text-slate-500">{s.notes || "—"}</td>
                      {!isTechRole && (
                        <td className="p-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => deleteSupply(s.id)} data-testid={`sup-del-${s.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Documenti tecnico (POS, attestati) — visibile anche al tecnico (read-only) */}
      {tech && (
        <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="docs-card">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-red-600" />
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                {isTechRole ? "I miei documenti" : t("auto.documenti_del_tecnico")}
              </div>
              <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600" data-testid="docs-count">{documents.length}</span>
            </div>
            {!isTechRole && (
              <Dialog open={docOpen} onOpenChange={setDocOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="btn-upload-doc">
                  <Plus className="mr-1 h-3.5 w-3.5" /> {t("auto.carica_documento")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuovo documento per {selectedTech?.name}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Tipo documento</Label>
                    <Select value={docForm.category} onValueChange={(v) => setDocForm({ ...docForm, category: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="doc-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DOC_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Titolo *</Label>
                    <Input value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} placeholder="es. POS lavori 2026 / Attestato altezze" className="rounded-sm" data-testid="doc-title" />
                  </div>
                  <div>
                    <Label>{t("common.description")}</Label>
                    <Input value={docForm.description} onChange={(e) => setDocForm({ ...docForm, description: e.target.value })} placeholder="Opzionale" className="rounded-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Data emissione</Label>
                      <Input type="date" value={docForm.issued_date} onChange={(e) => setDocForm({ ...docForm, issued_date: e.target.value })} className="rounded-sm" data-testid="doc-issued" />
                    </div>
                    <div>
                      <Label>{t("auto.scadenza_x")}</Label>
                      <Input type="date" value={docForm.expiry_date} onChange={(e) => setDocForm({ ...docForm, expiry_date: e.target.value })} className="rounded-sm" data-testid="doc-expiry" />
                    </div>
                  </div>
                  <div>
                    <Label>File (PDF, JPG, PNG) *</Label>
                    <Input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*" onChange={(e) => setDocForm({ ...docForm, file: e.target.files?.[0] || null })} className="rounded-sm" data-testid="doc-file" />
                    <div className="mt-1 text-[11px] text-slate-500">Max 20 MB</div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={uploadDocument} disabled={docUploading} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="doc-save">
                    {docUploading ? t("auto.caricamento_ellipsis") : t("auto.carica_btn")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            )}
          </div>

          {documents.length === 0 && (
            <div className="rounded-sm border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
              {t("auto.no_docs_tecnico_hint")}
            </div>
          )}

          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {documents.map((d) => {
              const cat = DOC_CATEGORIES.find((c) => c.value === d.category) || DOC_CATEGORIES[DOC_CATEGORIES.length - 1];
              const CatIcon = cat.icon;
              const exp = expiryStatus(d.expiry_date);
              return (
                <div key={d.id} className={`flex items-start gap-3 rounded-sm border p-3 ${cat.color}`} data-testid={`doc-row-${d.id}`}>
                  <CatIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{cat.label.split(" ")[0]}</span>
                      {exp && <span className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold ${exp.cls}`} data-testid={`doc-expiry-${d.id}`}>{exp.label}</span>}
                    </div>
                    <div className="mt-0.5 truncate text-sm font-semibold text-slate-900">{d.title}</div>
                    {d.description && <div className="truncate text-xs text-slate-600">{d.description}</div>}
                    <div className="mt-0.5 flex flex-wrap gap-2 text-[11px] text-slate-500">
                      {d.issued_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {t("auto.emesso_prefix")} {d.issued_date}</span>}
                      {d.expiry_date && <span className="inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> {t("auto.scad_prefix")} {d.expiry_date}</span>}
                      <span className="font-mono">{(d.filename || "").slice(-32)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)} data-testid={`doc-download-${d.id}`} title="Scarica">
                      <Download className="h-4 w-4 text-slate-700" />
                    </Button>
                    {!isTechRole && (
                      <Button size="sm" variant="ghost" onClick={() => deleteDocument(d.id)} data-testid={`doc-delete-${d.id}`} title={t("auto.elimina")}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {/* Attrezzatura assegnata */}
      {tech && (
        <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="equipment-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench className="h-4 w-4 text-blue-600" />
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.attrezzatura_assegnata_kicker")}</div>
              <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600" data-testid="equipment-count">{activeEquipment.length}</span>
            </div>
            <a href="/attrezzatura" className={`text-xs font-semibold text-[#0052FF] hover:underline ${isTechRole ? "hidden" : ""}`} data-testid="link-attrezzatura">
              {t("auto.gestisci_attrezzatura_link")}
            </a>
          </div>
          {activeEquipment.length === 0 ? (
            <div className="rounded-sm border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
              {t("auto.no_equipment_hint")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="p-2">{t("common.category")}</th>
                    <th className="p-2">{t("auto.nome_marca_modello")}</th>
                    <th className="p-2">{t("auto.seriale_col")}</th>
                    <th className="p-2">{t("auto.assegnata_il")}</th>
                    {!isTechRole && <th className="p-2 text-right">{t("common.actions")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {activeEquipment.map((eq) => (
                    <tr key={eq.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`equip-row-${eq.id}`}>
                      <td className="p-2 text-xs uppercase">{eq.category}</td>
                      <td className="p-2">
                        <div className="font-semibold text-slate-900">{eq.name}</div>
                        <div className="text-xs text-slate-500">{[eq.brand, eq.model].filter(Boolean).join(" · ") || "—"}</div>
                      </td>
                      <td className="p-2 font-mono text-xs">{eq.serial || "—"}</td>
                      <td className="p-2 font-mono text-xs text-slate-500">{(eq.assigned_at || "").slice(0, 10) || "—"}</td>
                      {!isTechRole && (
                        <td className="p-2">
                          <div className="flex flex-wrap items-center justify-end gap-1">
                            <Button size="sm" variant="outline" onClick={() => returnEquipment(eq.id)} className="h-7 rounded-sm text-xs" data-testid={`equip-return-${eq.id}`}>
                              <ArrowLeftCircle className="mr-1 h-3 w-3" /> {t("auto.rientro_btn")}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEquipmentStatus(eq.id, "repair", "Rotta")} className="h-7 rounded-sm border-amber-400 text-xs text-amber-700 hover:bg-amber-50" data-testid={`equip-broken-${eq.id}`} title="Marca come rotta / in riparazione">
                              🔧 Rotto
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEquipmentStatus(eq.id, "lost", "Persa")} className="h-7 rounded-sm border-red-400 text-xs text-red-700 hover:bg-red-50" data-testid={`equip-lost-${eq.id}`} title="Marca come persa">
                              ❓ Perso
                            </Button>
                            {user?.role === "admin" && (
                              <Button size="sm" onClick={() => generateDisciplinaryLetter(eq)} className="h-7 rounded-sm bg-slate-900 text-xs text-white hover:bg-slate-700" data-testid={`equip-charge-${eq.id}`} title="Genera lettera disciplinare + addebito (AI)">
                                € Addebito
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Attrezzatura ROTTA / PERSA (in attesa di addebito o dismissione) */}
      {tech && brokenLostEquipment.length > 0 && (
        <div className="rounded-sm border border-amber-200 bg-amber-50/40 p-5" data-testid="equipment-broken-lost-card">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-amber-800">Attrezzatura persa / rotta</div>
            <span className="rounded-sm bg-amber-100 px-1.5 py-0.5 font-mono text-xs text-amber-900" data-testid="equipment-broken-count">{brokenLostEquipment.length}</span>
          </div>
          <p className="mb-3 text-xs text-slate-600">
            Attrezzatura ancora imputata al tecnico ma marcata come persa o rotta. Da qui puoi generare l'addebito o rimetterla in magazzino.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-amber-200 text-left text-xs font-bold uppercase tracking-widest text-amber-700">
                <tr>
                  <th className="p-2">Stato</th>
                  <th className="p-2">{t("auto.nome_marca_modello")}</th>
                  <th className="p-2">{t("auto.seriale_col")}</th>
                  <th className="p-2">Data</th>
                  {!isTechRole && <th className="p-2 text-right">{t("common.actions")}</th>}
                </tr>
              </thead>
              <tbody>
                {brokenLostEquipment.map((eq) => {
                  const isLost = eq.status === "lost";
                  const when = (isLost ? eq.lost_at : eq.repair_at) || eq.updated_at || "";
                  return (
                    <tr key={eq.id} className="border-b border-amber-100 hover:bg-white" data-testid={`equip-bl-row-${eq.id}`}>
                      <td className="p-2 text-xs">
                        {isLost
                          ? <span className="rounded-sm bg-red-100 px-2 py-0.5 font-bold uppercase text-red-800">❓ Persa</span>
                          : <span className="rounded-sm bg-amber-100 px-2 py-0.5 font-bold uppercase text-amber-800">🔧 Rotta</span>}
                      </td>
                      <td className="p-2">
                        <div className="font-semibold text-slate-900">{eq.name}</div>
                        <div className="text-xs text-slate-500">{[eq.brand, eq.model].filter(Boolean).join(" · ") || "—"}</div>
                      </td>
                      <td className="p-2 font-mono text-xs">{eq.serial || "—"}</td>
                      <td className="p-2 font-mono text-xs text-slate-500">{(when || "").slice(0, 10) || "—"}</td>
                      {!isTechRole && (
                        <td className="p-2">
                          <div className="flex flex-wrap items-center justify-end gap-1">
                            <Button size="sm" variant="outline" onClick={() => setEquipmentStatus(eq.id, "warehouse", "In magazzino")} className="h-7 rounded-sm text-xs" data-testid={`equip-bl-return-${eq.id}`} title="Rientro in magazzino (chiude il caso)">
                              <ArrowLeftCircle className="mr-1 h-3 w-3" /> Rientra
                            </Button>
                            {user?.role === "admin" && (
                              <Button size="sm" onClick={() => generateDisciplinaryLetter(eq)} className="h-7 rounded-sm bg-slate-900 text-xs text-white hover:bg-slate-700" data-testid={`equip-bl-charge-${eq.id}`} title="Genera lettera disciplinare + addebito (AI)">
                                € Addebito
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}


      {/* Storico Bolle di consegna */}
      {tech && (
        <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="bolle-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileSignature className="h-4 w-4 text-emerald-600" />
              <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.bolle_di_consegna_ddt_kicker")}</div>
              <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600" data-testid="bolle-count">{bolle.length}</span>
            </div>
            <Button size="sm" onClick={() => setBollaOpen(true)} className={`rounded-sm bg-emerald-600 hover:bg-emerald-700 ${isTechRole ? "hidden" : ""}`} data-testid="btn-nuova-bolla-inline">
              <Plus className="mr-1 h-3.5 w-3.5" /> {t("auto.nuova_bolla_short_btn")}
            </Button>
          </div>

          {bolle.length === 0 ? (
            <div className="rounded-sm border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
              {t("auto.nessuna_bolla_tecnico_hint")}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-xs text-slate-500">Periodo:</Label>
                <Input
                  type="date"
                  value={bolleDateFrom}
                  onChange={(e) => setBolleDateFrom(e.target.value)}
                  className="h-8 w-40 rounded-sm text-xs"
                  data-testid="bolle-date-from"
                />
                <span className="text-xs text-slate-400">→</span>
                <Input
                  type="date"
                  value={bolleDateTo}
                  onChange={(e) => setBolleDateTo(e.target.value)}
                  className="h-8 w-40 rounded-sm text-xs"
                  data-testid="bolle-date-to"
                />
                {(bolleDateFrom || bolleDateTo) && (
                  <Button size="sm" variant="ghost" onClick={() => { setBolleDateFrom(""); setBolleDateTo(""); }} className="h-8 rounded-sm text-xs" data-testid="bolle-date-clear">
                    <X className="h-3.5 w-3.5 mr-1" /> Reset
                  </Button>
                )}
                <span className="ml-auto text-xs text-slate-500">Visibili: <span className="font-mono font-semibold text-slate-900">{filteredBolle.length}</span> / {bolle.length}</span>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="p-2">{t("common.date")}</th>
                    <th className="p-2">Rif.</th>
                    <th className="p-2 text-center">Articoli</th>
                    <th className="p-2">{t("auto.stato_firma")}</th>
                    <th className="p-2">{t("common.email")}</th>
                    <th className="p-2 text-right">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBolle.map((b) => (
                    <tr key={b.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`bolla-row-${b.id}`}>
                      <td className="p-2 font-mono text-xs">{(b.created_at || "").slice(0, 10)}</td>
                      <td className="p-2 font-mono text-xs">{b.id.slice(0, 8).toUpperCase()}</td>
                      <td className="p-2 text-center font-mono">{(b.items || []).length}</td>
                      <td className="p-2">
                        {b.signed_at ? (
                          <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                            <CheckCircle2 className="h-3 w-3" /> firmata
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-sm border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700">
                            <Clock className="h-3 w-3" /> in attesa
                          </span>
                        )}
                      </td>
                      <td className="p-2 text-xs text-slate-500">{b.email_sent_at ? (b.email_sent_at || "").slice(0, 16).replace("T", " ") : "—"}</td>
                      <td className="p-2 text-right">
                        <div className="flex justify-end gap-1">
                          {!b.signed_at && !isTechRole && (
                            <Button size="sm" variant="outline" onClick={() => setSigningBid(b.id)} className="h-7 rounded-sm text-xs" data-testid={`bolla-sign-${b.id}`}>
                              <FileSignature className="mr-1 h-3 w-3" /> Firma
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => downloadBollaPdf(b.id)} className="h-7" data-testid={`bolla-pdf-${b.id}`}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}


      <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 p-3">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.seriali_in_viaggio")}</div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Label className="text-xs text-slate-500">Periodo</Label>
            <Input
              type="date"
              value={serialDateFrom}
              onChange={(e) => setSerialDateFrom(e.target.value)}
              className="h-8 w-40 rounded-sm text-xs"
              data-testid="viaggiante-date-from"
            />
            <span className="text-xs text-slate-400">→</span>
            <Input
              type="date"
              value={serialDateTo}
              onChange={(e) => setSerialDateTo(e.target.value)}
              className="h-8 w-40 rounded-sm text-xs"
              data-testid="viaggiante-date-to"
            />
            {(serialDateFrom || serialDateTo) && (
              <Button size="sm" variant="ghost" onClick={() => { setSerialDateFrom(""); setSerialDateTo(""); }} className="h-8 rounded-sm text-xs" data-testid="viaggiante-date-clear">
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
            <Label className="text-xs text-slate-500">Committente</Label>
            <Select value={serialCommittenteFilter} onValueChange={setSerialCommittenteFilter}>
              <SelectTrigger className="h-8 w-48 rounded-sm text-xs" data-testid="viaggiante-committente-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__all__">Tutti i committenti</SelectItem>
                {uniqueCommittenti.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Label className="text-xs text-slate-500">{t("auto.filtra_materiale")}</Label>
            <Select value={serialMaterialFilter} onValueChange={setSerialMaterialFilter}>
              <SelectTrigger className="h-8 w-56 rounded-sm text-xs" data-testid="viaggiante-material-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__all__">{t("auto.tutti_i_materiali")}</SelectItem>
                {serialMaterialsInList.map((name) => <SelectItem key={name} value={name}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">Seriale</th>
              <th className="p-3">{t("common.material")}</th>
              <th className="p-3">{t("auto.committente_x")}</th>
              <th className="p-3">{t("auto.assegnato_il")}</th>
              <th className="p-3">{t("common.status")}</th>
              {!isTechRole && <th className="p-3 text-right">{t("common.actions")}</th>}
            </tr>
          </thead>
          <tbody>
            {filteredSerials.length === 0 && <tr><td colSpan={isTechRole ? 5 : 6} className="p-8 text-center text-slate-400">{t("auto.nessun_seriale_in_viaggio")}</td></tr>}
            {filteredSerials.map((s) => (
              <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="p-3 font-mono font-medium text-slate-900">{s.serial}</td>
                <td className="p-3">{s.material_name}</td>
                <td className="p-3 text-xs">
                  {s.committente_name ? (
                    <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-semibold uppercase tracking-wider text-slate-700">{s.committente_name}</span>
                  ) : <span className="italic text-slate-400">—</span>}
                </td>
                <td className="p-3 font-mono text-xs text-slate-500">{(s.moved_at || s.assigned_at || s.created_at || "").slice(0, 10)}</td>
                <td className="p-3">
                  {isTechRole ? (
                    <span className="inline-flex items-center gap-1 rounded-sm border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[11px] font-semibold uppercase text-blue-700">
                      {s.status === "traveling" ? "In viaggio" : s.status === "installed" ? "Installato" : s.status === "lost" ? "Perso" : s.status}
                    </span>
                  ) : (
                    <Select value={s.status} onValueChange={(v) => changeSerialStatus(s.id, v)}>
                      <SelectTrigger className="h-8 rounded-sm text-xs" data-testid={`status-select-${s.serial}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="traveling">{t("auto.in_viaggio")}</SelectItem>
                        <SelectItem value="installed">{t("auto.installato")}</SelectItem>
                        <SelectItem value="lost">{t("auto.perso")}</SelectItem>
                        <SelectItem value="warehouse">{t("auto.rientro_magazzino")}</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </td>
                {!isTechRole && (
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditSerial({ id: s.id, serial: s.serial, material_name: s.material_name || "", committente_id: s.committente_id || "" })}
                        className="rounded-sm"
                        data-testid={`edit-${s.serial}`}
                        title="Modifica materiale/committente"
                      >
                        <Pencil className="h-4 w-4 text-blue-600" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => doUnload(s.serial)} className="rounded-sm" data-testid={`return-${s.serial}`}>
                        Rientro magazzino
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteSerial(s)}
                        className="rounded-sm"
                        data-testid={`delete-${s.serial}`}
                        title="Elimina definitivamente"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog conflitto seriale già esistente */}
      <AlertDialog open={!!conflictDialog} onOpenChange={(o) => { if (!o) { setConflictDialog(null); focusAddSerialInput(); } }}>
        <AlertDialogContent data-testid="conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-amber-600" /> Seriale già presente
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <div>
                  Il seriale <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono font-semibold">{conflictDialog?.info.serial}</span> è già registrato:
                </div>
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm">
                  {conflictDialog?.info.status === "warehouse" && (
                    <div>📦 <b>{t("auto.in_magazzino_generale")}</b></div>
                  )}
                  {conflictDialog?.info.status === "traveling" && (
                    <div>🚛 <b>Sul furgone di:</b> {conflictDialog?.info.technician_name || "—"}</div>
                  )}
                  {conflictDialog?.info.status === "lost" && (
                    <div className="text-red-700">❌ <b>{t("auto.perso")}</b></div>
                  )}
                  <div className="mt-1 text-xs text-slate-500">
                    Materiale: {conflictDialog?.info.material_name}
                  </div>
                </div>
                <div>Vuoi spostarlo sul furgone di <b>{selectedTech?.name}</b>?</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="conflict-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmTransfer} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="conflict-transfer">
              <ArrowRightLeft className="mr-1 h-4 w-4" /> Sposta qui
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Modale creazione bolla */}
      <BollaModal
        open={bollaOpen}
        onOpenChange={setBollaOpen}
        techId={tech}
        techName={selectedTech?.name}
        techEmail={selectedTech?.email}
        serials={serials}
        materials={materials}
        supplies={supplies}
        equipment={equipment}
        onCreated={() => load(tech)}
      />

      {/* Modale firma bolla esistente */}
      <SignExistingBollaDialog
        bid={signingBid}
        onOpenChange={(o) => { if (!o) setSigningBid(null); }}
        onSign={signExistingBolla}
        techEmail={selectedTech?.email}
      />

      {/* Modale modifica seriale (materiale + committente) */}
      <Dialog open={!!editSerial} onOpenChange={(o) => { if (!o) setEditSerial(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifica seriale <span className="font-mono">{editSerial?.serial}</span></DialogTitle>
          </DialogHeader>
          {editSerial && (
            <div className="space-y-4 py-2">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Seriale</Label>
                <Input
                  value={editSerial.serial || ""}
                  onChange={(e) => setEditSerial((es) => ({ ...es, serial: e.target.value }))}
                  placeholder="Es. SN-XXXXX"
                  className="rounded-sm font-mono"
                  data-testid="edit-serial-input"
                />
                <div className="text-[11px] text-slate-500">
                  💡 Correggi qui se il barcode è stato letto male. Il sistema verifica che non ci siano duplicati.
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Materiale</Label>
                <Select
                  value={editSerial.material_name || ""}
                  onValueChange={(v) => setEditSerial((e) => ({ ...e, material_name: v }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="edit-material"><SelectValue placeholder="Scegli materiale…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {materials.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input
                  value={editSerial.material_name || ""}
                  onChange={(e) => setEditSerial((es) => ({ ...es, material_name: e.target.value }))}
                  placeholder="…oppure scrivi un nome custom"
                  className="mt-1 rounded-sm text-xs"
                  data-testid="edit-material-custom"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Committente</Label>
                <Select
                  value={editSerial.committente_id || "__none__"}
                  onValueChange={(v) => setEditSerial((e) => ({ ...e, committente_id: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="edit-committente"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">— Nessuno —</SelectItem>
                    {committenti.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditSerial(null)} disabled={savingEdit}>Annulla</Button>
            <Button onClick={() => saveEditSerial()} disabled={savingEdit || !editSerial?.serial?.trim() || !editSerial?.material_name?.trim()} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="edit-serial-save">
              {savingEdit ? "Salvataggio…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma: seriale già in magazzino generale → spostarlo su viaggiante */}
      <AlertDialog open={!!editConflict} onOpenChange={(o) => { if (!o) setEditConflict(null); }}>
        <AlertDialogContent data-testid="edit-conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-amber-600" /> Seriale presente in Magazzino Generale
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <div>
                  Il seriale <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono font-semibold">{editConflict?.newSerial}</span> è già registrato in <b>Magazzino Generale</b>.
                </div>
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-sm">
                  <div>📦 <b>Materiale:</b> {editConflict?.existing?.material_name || "—"}</div>
                </div>
                <div className="pt-1 text-xs text-slate-600">
                  Confermando, il record in magazzino generale viene <b>rimosso</b> e il seriale viene aggiornato sul furgone di <b>{selectedTech?.name || "questo tecnico"}</b>.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="edit-conflict-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => saveEditSerial({ forceReplace: true })} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="edit-conflict-confirm">
              Sposta su Magazzino Viaggiante
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// --- Mini dialog per firmare una bolla già emessa ---
function SignExistingBollaDialog({ bid, onOpenChange, onSign, techEmail }) {
  const { t } = useTranslation();
  const sigRef = useRef(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (bid) setSendEmail(true); }, [bid]);

  const submit = async () => {
    if (!bid) return;
    if (sigRef.current?.isEmpty()) { toast.error(t("auto.firma_vuota")); return; }
    setSaving(true);
    try {
      let dataUrl;
      try {
        dataUrl = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
      } catch (err) {
        dataUrl = sigRef.current.toDataURL("image/png");
      }
      await onSign(bid, dataUrl, sendEmail);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!bid} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-emerald-600" /> Firma bolla {bid ? bid.slice(0, 8).toUpperCase() : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div className="rounded-sm border-2 border-dashed border-slate-300 bg-slate-50 p-1">
            {bid && (
              <SignatureCanvas
                ref={sigRef}
                penColor="#0f172a"
                canvasProps={{
                  width: 640,
                  height: 180,
                  className: "w-full rounded-sm bg-white",
                  "data-testid": "sign-existing-canvas",
                }}
              />
            )}
          </div>
          <div className="flex items-center justify-between">
            <button type="button" onClick={() => sigRef.current?.clear()} className="text-xs text-slate-500 hover:text-slate-900" data-testid="sign-existing-clear">
              <Eraser className="mr-1 inline h-3 w-3" /> Cancella
            </button>
            <label className="flex items-center gap-2 text-xs">
              <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(!!v)} data-testid="sign-existing-email" />
              Invia email a <b>{techEmail || "—"}</b>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="sign-existing-submit">
            {saving ? "Salvataggio…" : "Firma"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}